// Central data for services / work items, projects and track record.
import {
  Layers,
  Blocks,
  Server,
  Cloud,
  Database,
  BarChart3,
  Bot,
} from "lucide-react";

export const SERVICES = [
  {
    id: "modernization",
    index: "01",
    icon: Layers,
    title: ".NET Modernization",
    tagline: "Legacy to leading-edge",
    summary:
      "Bring an aging Web Forms, VB.NET or older .NET app up to modern .NET Core 7–10 — without losing the business logic your team depends on.",
    points: [
      "Web Forms & VB.NET → Blazor",
      ".NET Framework → .NET Core 8/10",
      "Desktop apps → web apps",
      "Smooth, low-risk migration",
    ],
  },
  {
    id: "blazor",
    index: "02",
    icon: Blocks,
    title: "Blazor App Development",
    tagline: "Server & WebAssembly",
    summary:
      "Fast, responsive web applications built with Blazor — internal tools, customer portals and dashboards tailored to how your business actually works.",
    points: [
      "Internal tools & portals",
      "Custom component design",
      "Responsive on any device",
      "Real-time updates",
    ],
  },
  {
    id: "api",
    index: "03",
    icon: Server,
    title: "RESTful Web APIs",
    tagline: "RESTful .NET Core APIs",
    summary:
      "Well-structured, RESTful .NET Core Web APIs built with EF Core, OData and Dapper — clean, versioned and documented endpoints so your sites, apps and integrations all talk to each other reliably.",
    points: [
      "RESTful API design & standards",
      "EF Core, OData & Dapper",
      "Versioned, documented endpoints",
      "Secure third-party integrations",
    ],
  },
  {
    id: "azure",
    index: "04",
    icon: Cloud,
    title: "Azure Cloud & Hosting",
    tagline: "Ship with confidence",
    summary:
      "Get your app online and keep it running smoothly on Azure — with logins, automated deploys and hosting sized to fit your needs.",
    points: [
      "Azure App Services hosting",
      "Secure logins (Azure AD)",
      "Automated deploys (CI/CD)",
      "Right-sized for your scale",
    ],
  },
  {
    id: "database",
    index: "05",
    icon: Database,
    title: "Database Engineering",
    tagline: "Your data, organized",
    summary:
      "SQL Server design, clean-up and reporting queries — plus safe migrations when you're moving off spreadsheets or an old system.",
    points: [
      "SQL Server schema design",
      "Stored procedures & views",
      "Data clean-up & migration",
      "Performance tuning",
    ],
  },
  {
    id: "reporting",
    index: "06",
    icon: BarChart3,
    title: "Reporting & Dashboards",
    tagline: "See what matters",
    summary:
      "Turn your data into clear reports and dashboards with SSRS, Telerik and Power BI — so you can make decisions with confidence.",
    points: [
      "SSRS & Telerik reporting",
      "Power BI dashboards",
      "Automated report emails",
      "Secure audit logging",
    ],
  },
  {
    id: "ai-automation",
    index: "07",
    icon: Bot,
    title: ".NET AI Automation Setup",
    tagline: "AI-powered .NET workflows",
    summary:
      "Set up AI-driven automation inside your .NET stack — from GitHub Copilot rollout to Semantic Kernel and direct Claude / GPT / Gemini APIs powering agents, code review and everyday developer workflows.",
    points: [
      "GitHub Copilot rollout & guardrails",
      "Semantic Kernel & OpenAI SDKs",
      "Claude, GPT & Gemini API integration",
      "AI-assisted CI/CD & code review",
    ],
  },
];

// Options for the contact-form service multi-select.
export const SERVICE_OPTIONS = [
  ".NET Modernization",
  "Blazor App Development",
  "RESTful Web APIs",
  "Azure Cloud & Hosting",
  "Database Engineering",
  "Reporting & Dashboards",
  ".NET AI Automation Setup",
  "Bug Fixes / Small Changes",
  "Full Site Upgrade",
  "Not sure yet",
];

// Resume file served from /public (also send-to-employer friendly).
export const RESUME_URL = "/Shawn_Rye_Resume_2026.pdf";

// Selected projects from real roles (portfolio for employers).
export const PROJECTS = [
  {
    id: "healthcare-saas",
    org: "Texican Inc.",
    year: "2021–2022",
    title: "Healthcare Revenue-Cycle SaaS Platform",
    role: "Lead Architect / Senior Developer",
    description:
      "Architected and led a multi-tenant SaaS platform for healthcare revenue-cycle account management on Azure — from database design to deployment and scaling for clients.",
    tags: [".NET 6", "Blazor Server", "SQL", "Azure", "Web API", "Dapper"],
  },
  {
    id: "juvenile-case",
    org: "Choctaw Nation",
    year: "2023–2024",
    title: "Juvenile Case Management System",
    role: "Senior .NET Core Developer",
    description:
      "Built a Blazor Server application for juvenile case management, with 30+ Web APIs and stored procedures to streamline operations and keep data accurate.",
    tags: ["Blazor Server", "ASP.NET Core 6", "EF Core", "Dapper", "Azure DevOps"],
  },
  {
    id: "comcast-modernization",
    org: "Comcast",
    year: "2024–2025",
    title: "Web Forms → .NET 8/9 Modernization",
    role: "Senior Lead Developer III",
    description:
      "Modernized legacy .NET Web Forms applications into .NET 8/9 Blazor WebAssembly, adding real-time updates and integrated reporting while mentoring the team.",
    tags: ["Blazor WASM", ".NET 8", "EF Core", "SignalR", "Telerik", "Azure AD"],
  },
  {
    id: "amg-desktop",
    org: "AMG Financial",
    year: "2025",
    title: "Desktop App → Blazor WASM Rebuild",
    role: "Senior Blazor Engineer",
    description:
      "Migrated a .NET Framework 4.8 desktop application to a modern .NET 10 Blazor WebAssembly app, consuming OData APIs hosted in Azure API Management.",
    tags: ["Blazor WASM", ".NET 10", "OData", "Azure API Mgmt", "Entra ID"],
  },
  {
    id: "healthcare-archives",
    org: "Huron Consulting",
    year: "2020–2021",
    title: "Nationwide Healthcare Archives",
    role: "Senior .NET Developer",
    description:
      "Developed a Blazor Server archive platform for hospitals, including PDF generation and data conversions from systems like Meditech, Soarian and Epic.",
    tags: ["Blazor Server", ".NET 6", "Razor Pages", "Dapper", "AWS / IIS"],
  },
  {
    id: "symbotic-reporting",
    org: "Symbotic Robotics",
    year: "2024",
    title: "Automation Reporting & Data Tools",
    role: "Senior C# Developer",
    description:
      "Created C# tooling and detailed Excel reporting for the robotics process-controls team, syncing data between SharePoint and SQL via a Windows service.",
    tags: ["C#", ".NET 8", "EPPlus", "SharePoint CSOM", "SQL", "Dapper"],
  },
];

export const MANIFESTO = [
  {
    num: "01",
    title: "Built for outcomes, not billable hours.",
    body: "You get a straight answer, a fair price and software that fits your business — not an enterprise process wrapped around a simple problem.",
  },
  {
    num: "02",
    title: "You work directly with the engineer.",
    body: "No account managers, no handoffs. The person scoping your project is the person writing the code — so nothing gets lost in translation.",
  },
  {
    num: "03",
    title: "Ship it right, keep it running.",
    body: "Clean, tested code and reliable deploys mean your site keeps working long after launch — and small changes stay small.",
  },
];

export const STATS = [
  { value: "13+", label: "Years experience" },
  { value: "1:1", label: "Work directly with me" },
  { value: ".NET 10", label: "Modern, current stack" },
  { value: "Azure", label: "Cloud & DevOps" },
];

export const MARQUEE_ITEMS = [
  "C#",
  ".NET CORE 10",
  "BLAZOR",
  "AZURE",
  "SQL SERVER",
  "EF CORE",
  "RESTFUL APIS",
  ".NET AI AUTOMATION",
  "GITHUB COPILOT",
  "CLAUDE OPUS",
  "CHATGPT",
  "GEMINI",
  "MODERNIZATION",
];

// AI tools used in day-to-day engineering (via GitHub Copilot & directly).
export const AI_TOOLS = [
  {
    id: "claude",
    name: "Claude",
    vendor: "Anthropic",
    models: "Opus & Sonnet",
    description:
      "My primary AI pair-programmer through GitHub Copilot. I lean on Claude Opus for deep reasoning through complex .NET architecture, and Sonnet for fast, high-quality code generation and large-scale refactors.",
    uses: [
      "Claude Opus for deep reasoning",
      "Code generation & refactoring",
      "Architecture & code review",
    ],
  },
  {
    id: "chatgpt",
    name: "ChatGPT",
    vendor: "OpenAI",
    models: "GPT-4 / GPT-5",
    description:
      "Fast prototyping, debugging and documentation. I use ChatGPT to turn requirements into working code and clear technical docs, then validate and harden the output.",
    uses: [
      "Prototyping & debugging",
      "Technical documentation",
      "Requirement breakdown",
    ],
  },
  {
    id: "gemini",
    name: "Gemini",
    vendor: "Google",
    models: "Gemini Pro",
    description:
      "Research, multimodal analysis, and a strong second opinion on tricky implementations across the full stack — a great cross-check on approach and edge cases.",
    uses: [
      "Research & analysis",
      "Code assistance",
      "Cross-checking solutions",
    ],
  },
];

// Legacy .NET Framework + full-stack JavaScript experience (the "old stack").
export const LEGACY_STACK = [
  {
    id: "webforms",
    title: "ASP.NET Web Forms",
    description:
      "Years of building and maintaining production Web Forms apps on .NET Framework 4.x — postbacks, view state, master pages, and user/server controls.",
  },
  {
    id: "mvc",
    title: "ASP.NET MVC",
    description:
      "Full MVC 5 applications with Razor views, controllers, model binding, routing and areas — the classic server-rendered .NET pattern.",
  },
  {
    id: "fullstack-js",
    title: "Full-Stack JavaScript",
    description:
      "jQuery, vanilla JavaScript, AJAX and hands-on DOM work powering interactive front-ends — backed by HTML5 & CSS3 (MCTS 70-480 certified).",
  },
  {
    id: "vb-csharp",
    title: "VB.NET & C#",
    description:
      "Fluent in both languages across legacy Web Forms, Windows desktop apps and older class libraries — I can read and refactor code others avoid.",
  },
  {
    id: "classic-asp",
    title: "Classic ASP & Services",
    description:
      "Maintained and migrated classic ASP sites and older SOAP / ASMX & WCF web services into modern replacements without losing functionality.",
  },
  {
    id: "ui-libs",
    title: "Legacy UI & Reporting",
    description:
      "Bootstrap, jQuery UI, Telerik and SSRS inside older frameworks — comfortable in the tooling real enterprise systems were built on.",
  },
];


